import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "../../../lib/http/axiosConfig";
import { useDemoScenario } from "../../../demo/DemoScenarioContext";
import {
  listDemoSections,
  removeDemoSection,
  renameDemoSection,
  saveDemoSection,
} from "../../../demo/demoSections";
import { useT } from "../../../i18n";

/**
 * 섹션 = 이름 붙여 저장한 셀 묶음. 농장 자산이라 단건 미션(불러오기)과 예약(구역 선택)이 같이 쓴다.
 * 저장은 지금 지도에서 고른 셀(공유 선택)을 그대로 보내고, 순서는 서버가 전체 스캔과 같은 지그재그로 정한다.
 */
const ALT_MIN = 1;
const ALT_MAX = 10;

export default function SectionPanel({ onChanged }) {
  const t = useT();
  // 데모는 섹션을 서버에 만들지 않는다. 시연 중 만든 묶음이 실제 농장에 남기 때문.
  // 자세한 이유는 demo/demoSections.js.
  const { isDemo } = useDemoScenario();
  const farmId = useSelector((state) => state.farm.farmId);
  const selectedCellIds = useSelector((state) => state.route.selectedCellIds);
  const [sections, setSections] = useState([]);
  const [name, setName] = useState("");
  const [altitude, setAltitude] = useState("4");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!farmId) return;
    if (isDemo) {
      setSections(listDemoSections(farmId));
      return;
    }
    try {
      const { data } = await axios.get(`/api/farms/${farmId}/sections`);
      setSections(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      console.warn("[SECTION] 목록 조회 실패", error);
    }
  }, [farmId, isDemo]);

  useEffect(() => {
    load();
  }, [load]);

  const notify = () => {
    load();
    onChanged?.();
  };

  const errorCode = (error) => error?.response?.data?.error?.code;

  const save = async () => {
    const trimmed = name.trim();
    const alt = Number(altitude);
    if (!trimmed) {
      toast.warn(t("scheduler.section.toast.nameRequired"), { toastId: "section-name" });
      return;
    }
    if (!(alt >= ALT_MIN && alt <= ALT_MAX)) {
      toast.warn(t("scheduler.section.toast.altitudeRange", { min: ALT_MIN, max: ALT_MAX }), { toastId: "section-alt" });
      return;
    }
    if (selectedCellIds.length === 0) {
      toast.warn(t("scheduler.section.toast.cellsRequired"), { toastId: "section-cells" });
      return;
    }
    setSaving(true);
    try {
      const data = isDemo
        ? { data: saveDemoSection(farmId, { name: trimmed, altitude: alt, cellIds: selectedCellIds }) }
        : (await axios.post(`/api/farms/${farmId}/sections`, {
            name: trimmed,
            altitude: alt,
            cellIds: selectedCellIds,
          })).data;
      setName("");
      toast.success(
        t("scheduler.section.toast.saved", { name: data?.data?.name ?? trimmed, count: selectedCellIds.length })
      );
      notify();
    } catch (error) {
      if (errorCode(error) === "SECTION-002") {
        toast.error(t("scheduler.section.toast.duplicateName"), { toastId: "section-dup" });
      } else {
        toast.error(t("scheduler.section.toast.saveFailed"), { toastId: "section-save" });
      }
    } finally {
      setSaving(false);
    }
  };

  const rename = async (section) => {
    const next = window.prompt(t("scheduler.section.prompt.rename"), section.name);
    if (next == null || !next.trim() || next.trim() === section.name) return;
    try {
      if (isDemo) renameDemoSection(farmId, section.id, next.trim());
      else await axios.patch(`/api/farms/${farmId}/sections/${section.id}`, { name: next.trim() });
      notify();
    } catch (error) {
      toast.error(
        errorCode(error) === "SECTION-002"
          ? t("scheduler.section.toast.duplicateName")
          : t("scheduler.section.toast.renameFailed"),
        { toastId: "section-rename" }
      );
    }
  };

  const remove = async (section) => {
    if (section.scheduleCount > 0) {
      toast.warn(t("scheduler.section.deleteBlocked", { name: section.name, count: section.scheduleCount }), {
        toastId: "section-in-use",
      });
      return;
    }
    if (!window.confirm(t("scheduler.section.confirm.delete", { name: section.name }))) return;
    try {
      if (isDemo) removeDemoSection(farmId, section.id);
      else await axios.delete(`/api/farms/${farmId}/sections/${section.id}`);
      notify();
    } catch (error) {
      toast.error(
        errorCode(error) === "SECTION-003"
          ? t("scheduler.section.toast.deleteInUse")
          : t("scheduler.section.toast.deleteFailed"),
        { toastId: "section-delete" }
      );
    }
  };

  const canSave = selectedCellIds.length > 0 && !saving;

  return (
    <section className="sb-card section-card">
      <div className="sb-title-with-help">
        <h3 className="sb-card-title">{t("scheduler.section.title")}</h3>
        <button
          type="button"
          className="sb-help"
          aria-label={t("scheduler.section.helpAria")}
          data-tip={t("scheduler.section.helpTip")}
        >
          ?
        </button>
      </div>

      <div className="sec-list">
        {sections.length === 0 && <div className="sec-empty">{t("scheduler.section.empty")}</div>}
        {sections.map((s) => (
          <div className="sec-row" key={s.id}>
            <div className="sec-text">
              <div className="sec-name">{s.name}</div>
              <div className="sec-sub">
                {t("scheduler.section.row.summary", { count: s.cellIds.length, altitude: s.altitude })}
                {s.scheduleCount > 0 ? t("scheduler.section.row.usedBy", { count: s.scheduleCount }) : ""}
              </div>
            </div>
            <div className="sec-acts">
              <button
                type="button"
                className="sec-btn icon"
                title={t("scheduler.section.row.rename")}
                aria-label={t("scheduler.section.row.rename")}
                onClick={() => rename(s)}
              >
                ✎
              </button>
              <button
                type="button"
                className="sec-btn icon danger"
                title={t("scheduler.section.row.delete")}
                aria-label={t("scheduler.section.row.delete")}
                onClick={() => remove(s)}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="sec-form">
        <div className="sec-form-row">
          <input
            className="sec-input"
            value={name}
            maxLength={30}
            placeholder={t("scheduler.section.form.namePlaceholder")}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSave) save();
            }}
          />
          <div className="sec-alt">
            <input
              className="sec-input num"
              type="number"
              min={ALT_MIN}
              max={ALT_MAX}
              step={1}
              value={altitude}
              aria-label={t("scheduler.section.form.altitude")}
              onChange={(e) => setAltitude(e.target.value)}
            />
            <span className="sec-unit">m</span>
          </div>
        </div>
        <button type="button" className="sec-save" disabled={!canSave} onClick={save}>
          {selectedCellIds.length > 0
            ? t("scheduler.section.form.saveSelected", { count: selectedCellIds.length })
            : t("scheduler.section.form.saveDisabled")}
        </button>
      </div>
    </section>
  );
}
